import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

function BarcodeScanner() {
  const navigate = useNavigate();
  const [manualInput, setManualInput] = useState('');
  const [scannerReady, setScannerReady] = useState(false);
  const [_scanning, setScanning] = useState(false); // eslint-disable-line no-unused-vars
  const [useManual, setUseManual] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const html5QrCodeRef = useRef(null);
  const lastScannedRef = useRef(null);

  useEffect(() => {
    // 카메라 스캔 모드일 때만 스캐너 초기화
    if (!useManual && !permissionDenied) {
      initScanner();
    }

    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch((err) => {
          console.log('스캐너 정지:', err);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useManual]);

  const initScanner = async () => {
    try {
      // 기존 인스턴스가 있고 실행 중이면 정리
      if (html5QrCodeRef.current) {
        try {
          if (html5QrCodeRef.current.isScanning) {
            await html5QrCodeRef.current.stop();
          }
          await html5QrCodeRef.current.clear();
        } catch (e) {
          console.log('기존 스캐너 정리:', e);
        }
        html5QrCodeRef.current = null;
      }

      // 보안 컨텍스트 확인 (HTTPS 또는 localhost)
      if (!window.isSecureContext) {
        throw new Error('카메라 접근은 HTTPS 또는 localhost 환경에서만 가능합니다.');
      }

      // 미디어 장치 API 지원 확인
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('이 브라우저는 카메라 접근을 지원하지 않습니다.');
      }

      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      console.log('=== 바코드 스캐너 초기화 시작 ===');

      // 먼저 카메라 권한 확인
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });

        // 권한 확인용 스트림 정지
        stream.getTracks().forEach(track => track.stop());
      } catch (permError) {
        console.error('카메라 권한 오류:', permError);
        throw new Error('카메라 권한이 거부되었습니다. 브라우저 설정에서 권한을 허용해주세요.');
      }

      // 모바일 감지 및 카메라 시작
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        await html5QrCode.start(
          { facingMode: { exact: "environment" } },
          config,
          onScanSuccess,
          onScanError
        ).catch(async () => {
          // 후면 카메라 강제 지정 실패 시 일반 모드로 재시도
          await html5QrCode.start(
            { facingMode: "environment" },
            config,
            onScanSuccess,
            onScanError
          );
        });
      } else {
        await html5QrCode.start(
          { facingMode: "environment" },
          config,
          onScanSuccess,
          onScanError
        );
      }

      setScannerReady(true);
      setScanning(true);
      setPermissionDenied(false);
    } catch (error) {
      console.error('❌ 스캐너 초기화 오류:', error);
      setPermissionDenied(true);
      setScannerReady(false);
      setScanning(false);

      // 에러 메시지를 상태로 저장하여 UI에 표시하면 더 좋음 (현재는 permissionDenied만 사용)
      alert(error.message); // 임시로 alert 사용하여 사용자에게 알림
    }
  };

  const onScanSuccess = async (decodedText) => {
    // 일시 정지 중이거나 같은 바코드를 연속으로 스캔한 경우 무시
    if (isPaused || lastScannedRef.current === decodedText) {
      return;
    }

    console.log('스캔 성공:', decodedText);
    lastScannedRef.current = decodedText;
    setIsPaused(true);
    await searchInventory(decodedText);
  };

  const onScanError = (error) => {
    // 스캔 오류는 무시 (계속 스캔)
  };

  const searchInventory = async (barcodeId) => {
    try {
      const inventoryRef = collection(db, 'inventory');
      const q = query(inventoryRef, where('barcodeId', '==', barcodeId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setErrorMessage(`해당 바코드를 찾을 수 없습니다: ${barcodeId}`);
        // 3초 후 에러 메시지 제거하고 스캔 재개
        setTimeout(() => {
          setErrorMessage('');
          setIsPaused(false);
          lastScannedRef.current = null;
        }, 3000);
        return;
      }

      const item = snapshot.docs[0];
      navigate(`/inventory/${item.id}`);
    } catch (error) {
      console.error('재고 검색 오류:', error);
      setErrorMessage('재고 검색 중 오류가 발생했습니다.');
      // 3초 후 에러 메시지 제거하고 스캔 재개
      setTimeout(() => {
        setErrorMessage('');
        setIsPaused(false);
        lastScannedRef.current = null;
      }, 3000);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      searchInventory(manualInput.trim());
    }
  };

  const toggleMode = () => {
    setUseManual(!useManual);
    setManualInput('');
    setPermissionDenied(false);
  };

  const retryCamera = async () => {
    setPermissionDenied(false);
    setScannerReady(false);
    setScanning(false);

    // 기존 스캐너 인스턴스 정리
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.log('스캐너 정리 중 오류 (무시 가능):', err);
      }
      html5QrCodeRef.current = null;
    }

    // 스캐너 재시작
    setTimeout(() => {
      initScanner();
    }, 100);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">바코드 스캔</h2>

      {/* 모드 전환 버튼 */}
      <div className="mb-6 text-center">
        <button
          onClick={toggleMode}
          className="px-6 py-2 bg-vintage-600 hover:bg-vintage-700 text-white rounded-lg transition"
        >
          {useManual ? '📱 카메라 스캔으로 전환' : '⌨️ 수동 입력으로 전환'}
        </button>
      </div>

      {/* 에러 메시지 표시 */}
      {errorMessage && (
        <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">❌</span>
              <p className="text-red-800 font-medium">{errorMessage}</p>
            </div>
            <button
              onClick={() => {
                setErrorMessage('');
                setIsPaused(false);
                lastScannedRef.current = null;
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-sm font-medium"
            >
              계속 스캔
            </button>
          </div>
          <p className="text-xs text-red-600 mt-2">3초 후 자동으로 스캔이 재개됩니다</p>
        </div>
      )}

      {/* 카메라 스캔 모드 */}
      {!useManual && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* 권한 거부 시 안내 */}
          {permissionDenied && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                📷 카메라 권한이 필요합니다
              </h3>

              <div className="space-y-4 text-red-700">
                <p className="font-semibold">카메라 스캔을 사용하려면 카메라 권한을 허용해주세요.</p>

                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <p className="font-bold text-red-900 mb-2">📱 iPhone 설정 방법:</p>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>iPhone <strong>설정</strong> 앱 열기</li>
                    <li><strong>Safari</strong> 찾기</li>
                    <li><strong>카메라</strong> 항목을 찾아서 <strong>"묻기"</strong> 또는 <strong>"허용"</strong>으로 변경</li>
                    <li>Safari 앱을 완전히 종료 후 다시 열기</li>
                    <li>이 페이지를 새로고침하면 권한 요청이 나타납니다</li>
                  </ol>
                </div>

                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <p className="font-bold text-red-900 mb-2">🤖 Android 설정 방법:</p>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>Chrome 주소창 왼쪽의 <strong>자물쇠 아이콘</strong> 또는 <strong>정보 아이콘</strong> 클릭</li>
                    <li><strong>권한</strong> 또는 <strong>사이트 설정</strong> 선택</li>
                    <li><strong>카메라</strong>를 <strong>"허용"</strong>으로 변경</li>
                    <li>페이지 새로고침</li>
                  </ol>
                </div>

                <button
                  onClick={retryCamera}
                  className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition mt-4"
                >
                  📷 카메라 권한 다시 요청하기
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition"
                >
                  🔄 권한 설정 후 새로고침
                </button>

                <button
                  onClick={() => setUseManual(true)}
                  className="w-full px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition"
                >
                  ⌨️ 수동 입력 모드로 전환
                </button>
              </div>
            </div>
          )}

          {/* 카메라 스캔 영역 */}
          {!permissionDenied && (
            <>
              <div className="mb-4 text-center">
                <p className="text-gray-600">
                  카메라를 바코드 또는 QR 코드에 맞춰주세요
                </p>
              </div>

              <div id="qr-reader" className="w-full"></div>

              {!scannerReady && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vintage-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">스캐너 초기화 중...</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 수동 입력 모드 */}
      {useManual && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              바코드 번호를 직접 입력하거나 USB 스캐너를 사용하세요
            </p>
          </div>

          <form onSubmit={handleManualSearch} className="space-y-4">
            <div>
              <input
                type="text"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="바코드 번호 입력 (예: ONE1234567)"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-vintage-500"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={!manualInput.trim()}
              className="w-full px-6 py-3 bg-vintage-600 hover:bg-vintage-700 text-white text-lg font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🔍 검색
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">💡 USB 스캐너 사용 방법</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>USB 바코드 스캐너를 컴퓨터에 연결하세요</li>
              <li>위 입력란을 클릭하여 포커스를 맞추세요</li>
              <li>바코드를 스캔하면 자동으로 입력됩니다</li>
              <li>Enter를 누르거나 검색 버튼을 클릭하세요</li>
            </ol>
          </div>
        </div>
      )}

      {/* 사용 가이드 */}
      <div className="mt-6 bg-vintage-50 rounded-lg p-4">
        <h3 className="font-semibold text-vintage-800 mb-2">📖 사용 가이드</h3>
        <ul className="text-sm text-vintage-700 space-y-1">
          <li>• <strong>모바일</strong>: 카메라 스캔 모드를 사용하세요</li>
          <li>• <strong>데스크톱</strong>: 수동 입력 또는 USB 스캐너를 사용하세요</li>
          <li>• QR 코드와 일반 바코드 모두 인식 가능합니다</li>
          <li>• 스캔 성공 시 자동으로 해당 재고 상세 페이지로 이동합니다</li>
        </ul>
      </div>
    </div>
  );
}

export default BarcodeScanner;
